import { Alert, Button, Descriptions, Tag } from "antd";
import { DollarOutlined, EditOutlined } from "@ant-design/icons";
import Popup from "../../components/Popup";
import PrismTruncatedText from "./PrismTruncatedText";
import PrismSubscriptionNotes from "./PrismSubscriptionNotes";
import {
  canManagePrismRecord,
  CRITICALITY_COLORS,
  CRITICALITY_LABELS,
  formatDate,
  formatLabel,
  formatOfficeName,
  formatSubOfficeName,
  getEffectiveSubscriptionStatus,
  isFullPrismRecord,
  STATUS_COLORS,
} from "./prismSubscriptionUtils";
import "../../styles/pages/prism/prismSubscriptionDetails.scss";

const renderEmpty = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;

const renderLabel = (value) =>
  value ? formatLabel(value) : "-";

const renderStatus = (value) =>
  value ? (
    <Tag color={STATUS_COLORS[value] || "blue"}>{formatLabel(value)}</Tag>
  ) : (
    "-"
  );

const renderCriticality = (value) =>
  value ? (
    <Tag
      color={CRITICALITY_COLORS[value] || "default"}
      style={
        value === "overdue"
          ? { borderColor: "#000", color: "#fff" }
          : undefined
      }
    >
      {CRITICALITY_LABELS[value] || formatLabel(value)}
    </Tag>
  ) : (
    "-"
  );

const renderAutoRenewal = (value) => (
  <Tag color={value ? "green" : "default"}>
    {value ? "Enabled" : "Disabled"}
  </Tag>
);

const PrismSubscriptionDetailsModal = ({
  details = {},
  onClose,
  onAddPayment,
  onEdit,
  open = false,
  subscription = null,
}) => {
  if (!subscription) return null;

  const hasFullAccess = isFullPrismRecord(subscription, details);
  const canManage = canManagePrismRecord(details, subscription);
  const canAddPayment = canManage && typeof onAddPayment === "function";
  const canEdit = canManage && typeof onEdit === "function";
  const hasManagementAction = canAddPayment || canEdit;
  const effectiveStatus = getEffectiveSubscriptionStatus(subscription);
  const isDisabled = effectiveStatus === "disabled";
  const subscriptionName =
    subscription.subscription_name || "Unnamed Subscription";
  const office = subscription.subscriber_department
    ? formatOfficeName(subscription.subscriber_department)
    : "-";
  const unit = subscription.subscriber_sub_department
    ? formatSubOfficeName(subscription.subscriber_sub_department)
    : "N/A (Office-level)";

  return (
    <Popup
      className="prism-subscription-details-popup"
      footer={[
        <Button
          key="close"
          onClick={onClose}
          type={hasManagementAction ? "default" : "primary"}
        >
          Close
        </Button>,
        ...(canEdit
          ? [
              <Button
                icon={<EditOutlined />}
                key="edit"
                onClick={() => onEdit(subscription)}
              >
                Edit Subscription
              </Button>,
            ]
          : []),
        ...(canAddPayment
          ? [
              <Button
                icon={<DollarOutlined />}
                key="payment"
                onClick={() => onAddPayment(subscription)}
                type="primary"
              >
                Add Payment
              </Button>,
            ]
          : []),
      ]}
      isOpen={open}
      onCancel={onClose}
      title={
        <PrismTruncatedText
          value={`Subscription Details — ${subscriptionName}`}
        />
      }
      width={900}
    >
      <div className="prism-subscription-details">
        {!hasFullAccess && (
          <Alert
            className="prism-subscription-details__restricted-alert"
            message="Additional subscription details are restricted to the assigned office or unit."
            showIcon
            type="info"
          />
        )}

        <section className="prism-subscription-details__section">
          <h3>Basic Information</h3>
          <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
            <Descriptions.Item
              label="Subscription Name"
            >
              {subscriptionName}
            </Descriptions.Item>
            {hasFullAccess && (
              <Descriptions.Item label="Availability">
                <Tag color={subscription.is_enabled === false ? "default" : "green"}>
                  {subscription.is_enabled === false ? "Disabled" : "Enabled"}
                </Tag>
              </Descriptions.Item>
            )}
            {hasFullAccess && (
              <Descriptions.Item label="Subscription Type">
                {renderLabel(subscription.subscription_type)}
              </Descriptions.Item>
            )}
            <Descriptions.Item
              label="Description"
              span={{ xs: 1, sm: 2 }}
            >
              <span className="prism-subscription-details__long-value">
                {renderEmpty(subscription.subscription_details)}
              </span>
            </Descriptions.Item>
            {hasFullAccess && (
              <Descriptions.Item
                label="Subscriber Email"
                span={{ xs: 1, sm: 2 }}
              >
                <span className="prism-subscription-details__long-value">
                  {renderEmpty(subscription.subscriber_email)}
                </span>
              </Descriptions.Item>
            )}
            {hasFullAccess && (
              <Descriptions.Item
                label="Additional Subscriber Emails"
                span={{ xs: 1, sm: 2 }}
              >
                <span className="prism-subscription-details__long-value">
                  {subscription.subscriber_additional_emails?.length
                    ? subscription.subscriber_additional_emails.join(", ")
                    : "-"}
                </span>
              </Descriptions.Item>
            )}
          </Descriptions>
        </section>

        <section className="prism-subscription-details__section">
          <h3>Office/Unit Assignment</h3>
          <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
            <Descriptions.Item label="Office">{office}</Descriptions.Item>
            <Descriptions.Item label="Unit">{unit}</Descriptions.Item>
          </Descriptions>
        </section>

        {hasFullAccess && (
          <>
            <section className="prism-subscription-details__section">
              <h3>Contract Information</h3>
              <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                <Descriptions.Item label="Criticality">
                  {isDisabled ? "-" : renderCriticality(subscription.criticality)}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {renderStatus(effectiveStatus)}
                </Descriptions.Item>
                <Descriptions.Item label="Contract Start Date">
                  {formatDate(subscription.contract_start_date)}
                </Descriptions.Item>
                <Descriptions.Item label="Contract End Date">
                  {formatDate(subscription.contract_end_date)}
                </Descriptions.Item>
                <Descriptions.Item label="Renewal Date">
                  {formatDate(subscription.contract_renewal_date)}
                </Descriptions.Item>
                <Descriptions.Item label="Renewal Frequency">
                  {renderLabel(subscription.contract_renewal_frequency)}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Auto-renewal"
                  span={{ xs: 1, sm: 2 }}
                >
                  {renderAutoRenewal(subscription.auto_renewal_status)}
                </Descriptions.Item>
              </Descriptions>
            </section>

            <section className="prism-subscription-details__section">
              <h3>Billing &amp; Account</h3>
              <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                <Descriptions.Item label="Billing Cycle">
                  {renderLabel(subscription.billing_cycle)}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Method">
                  {renderLabel(subscription.payment_method)}
                </Descriptions.Item>
                {subscription.payment_method === "bank_transfer" && (
                  <Descriptions.Item label="Bank Name">
                    {renderEmpty(subscription.bank_name)}
                  </Descriptions.Item>
                )}
                <Descriptions.Item
                  label="Payee Name"
                  span={{ xs: 1, sm: 2 }}
                >
                  <span className="prism-subscription-details__long-value">
                    {renderEmpty(subscription.account_holder_name)}
                  </span>
                </Descriptions.Item>
              </Descriptions>
            </section>

            {canManage && (
              <PrismSubscriptionNotes subscriptionId={subscription.id} />
            )}
          </>
        )}
      </div>
    </Popup>
  );
};

export default PrismSubscriptionDetailsModal;
